import moment from 'moment';
import classNames from 'classnames';
import Day from './Day';
import { CalendarEntryWithDate } from '../../../components/availabilityWidget/api/bookingsApi';

const weekdayNames = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export interface CalendarBooking {
  date: string;
  vac: boolean;
  arr: boolean;
}

interface WeekProps {
  bookings: CalendarBooking[];
  allBookings?: CalendarEntryWithDate;
  clickable: boolean;
  vacancyMode?: boolean;
  minDate?: moment.Moment;
  date: moment.Moment;
  month: moment.Moment;
  selected?: moment.Moment;
  onDaySelect: (day: moment.Moment) => void;
}

function isPreviousDayBooked(theDate: moment.Moment, allBookings?: CalendarEntryWithDate): boolean {
  if (!allBookings) {
    return false;
  }
  const key = moment(theDate).subtract(1, 'd').format('Y-MM-DD');
  const previousDay = allBookings[key];
  if (!previousDay) {
    return false;
  }
  return !previousDay.vac;
}

function isBooked(bookings: CalendarBooking[], theDate: moment.Moment): boolean {
  if (!bookings) {
    return false;
  }
  let booked = false;
  const weeklyBookings = bookings;
  for (let i = 0; i < weeklyBookings.length; i += 1) {
    const currentBooking = weeklyBookings[i];
    if (theDate.isSame(currentBooking.date, 'day') && currentBooking.vac === false) {
      booked = true;
      break;
    }
  }
  return booked;
}

function isArrival(bookings: CalendarBooking[], theDate: moment.Moment): boolean {
  if (!bookings) {
    return false;
  }
  let arrival = false;
  const allBookings = bookings;
  for (let i = 0; i < allBookings.length; i += 1) {
    const currentBooking = allBookings[i];
    if (theDate.isSame(currentBooking.date, 'day') && currentBooking.arr === true) {
      arrival = true;
      break;
    }
  }
  return arrival;
}

export default function Week({
  bookings,
  allBookings,
  clickable,
  minDate,
  date,
  month,
  selected,
  onDaySelect,
  vacancyMode,
}: WeekProps): JSX.Element {
  function onDayClick(day: moment.Moment): void {
    onDaySelect(day);
  }

  function isClickable(theDate: moment.Moment): boolean {
    if (minDate && theDate.isBefore(minDate.startOf('day'))) {
      return false;
    }
    if (vacancyMode && bookings) {
      return !!bookings.find((booking) => booking.date === theDate.format('YYYY-MM-DD'))?.vac;
    }
    if (bookings) {
      return isArrival(bookings, theDate);
    }
    return clickable;
  }

  const days = [];
  const dateIterator = date.clone();
  for (let i = 0; i < 7; i += 1) {
    const today = dateIterator.clone();
    const day = {
      name: today.format('dd').substring(0, 1),
      number: today.date(),
      isBooked: isBooked(bookings, today),
      isArrival: isArrival(bookings, today),
      isClickable: isClickable(today),
      isCurrentMonth: today.month() === month.month(),
      isToday: today.isSame(new Date(), 'day'),
      isOccured: today.isBefore(new Date(), 'day'),
      isDifferentMonth: today.month() !== month.month(),
      date: today,
      previousDayBooked: isPreviousDayBooked(today, allBookings),
      selected: today.isSame(selected),
    };
    isPreviousDayBooked(today, allBookings);
    const className = classNames(
      {
        today: today.isSame(new Date(), 'day'),
        'different-month': day.isDifferentMonth,
        selected: day.selected,
        'free-after-booked': !day.isArrival && !day.isBooked && day.previousDayBooked,
        'arrival-after-booked':
          !day.selected && day.isArrival && !day.isBooked && day.previousDayBooked,
        booked: day.isBooked && day.previousDayBooked,
        'booked-after-free': !day.isToday && day.isBooked && !day.previousDayBooked,
        arrival: day.isArrival && !vacancyMode,
        'not-clickable': !clickable,
        vacant: vacancyMode && day.isClickable,
        occured: today.isBefore(new Date(), 'day'),
      },
      `weekday-${weekdayNames[i]}`,
    );
    days.push(
      <Day
        key={day.date.toString()}
        className={className}
        onClick={day.isClickable ? (): void => onDayClick(day.date.clone()) : undefined}
        disabled={!day.isClickable || day.isDifferentMonth}
      >
        {day.number}
      </Day>,
    );

    dateIterator.add(1, 'd');
  }

  return (
    <div className="calendar-week" key={days[0].toString()}>
      {days}
    </div>
  );
}

const defaultProps: Pick<WeekProps, 'minDate' | 'selected' | 'allBookings' | 'vacancyMode'> = {
  minDate: undefined,
  selected: undefined,
  allBookings: undefined,
  vacancyMode: false,
};

Week.defaultProps = defaultProps;
